import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Skeleton,
  TextField,
  CircularProgress,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Chip,
  Autocomplete,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Add,
  People,
  MoreVert,
  Edit,
  Delete,
  Email,
  WhatsApp,
  Notifications as NotificationsIcon,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLimits } from '../hooks/useLimits';
import { getUserGuests, createGuest, deleteGuest, updateGuest, Guest as GuestType } from '../services/guestsService';
import { getUserEvents, Event } from '../services/eventsService';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { LimitReachedDialog } from '../components/subscription/LimitReachedDialog';
import { Dialog } from '../layout/Dialog';
import { useNavigate } from 'react-router-dom';
import { useSafeArea } from '../hooks/useSafeArea';
import { sendGuestNotification, sendBulkGuestNotifications, isHostlyUser } from '../services/notificationsService';
import allergiesData from '../data/alergies.json';

const GuestItem: React.FC<{
  guest: GuestType;
  onEdit: (guest: GuestType) => void;
  onDelete: (guest: GuestType) => void;
  onClick: (guestId: string) => void;
  onSendNotification?: (guest: GuestType) => void;
  theme: any;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (guestId: string) => void;
}> = ({ guest, onEdit, onDelete, onClick, onSendNotification, theme, isSelectionMode, isSelected, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isHostlyUserState, setIsHostlyUserState] = useState(false);

  useEffect(() => {
    const checkUser = async () => {
      if (guest.email) {
        const isUser = await isHostlyUser(guest.email);
        setIsHostlyUserState(isUser);
      }
    };
    checkUser();
  }, [guest.email]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    onEdit(guest);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    onDelete(guest);
  };

  const handleWhatsApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (guest.phone) {
      const cleanPhone = guest.phone.replace(/\D/g, '');
      const phone = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
      window.open(`https://wa.me/${phone}`, '_blank');
    }
  };

  const handleEmail = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (guest.email) {
      window.location.href = `mailto:${guest.email}`;
    }
  };

  const handleNotification = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSendNotification) {
      onSendNotification(guest);
    }
  };

  const handleClick = () => {
    if (isSelectionMode && onSelect) {
      onSelect(guest.id!);
    } else {
      onClick(guest.id!);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        backgroundColor: isSelected
          ? (theme.colors.secondary + '20') // 20 hex = ~12% opacity
          : theme.colors.card,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
          backgroundColor: isSelected
            ? (theme.colors.secondary + '30') // Slightly darker for hover
            : (theme.colors.secondary + '10'), // Light hover effect
        },
      }}
    >
      {isSelectionMode && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (onSelect) onSelect(guest.id!);
          }}
          sx={{
            mr: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            minHeight: 48,
            cursor: 'pointer',
          }}
        >
          <Checkbox
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              if (onSelect) onSelect(guest.id!);
            }}
            sx={{
              padding: '12px',
              '& .MuiSvgIcon-root': {
                fontSize: 28,
              },
            }}
          />
        </Box>
      )}
      <Avatar
        sx={{
          width: 56,
          height: 56,
          backgroundColor: theme.colors.secondary,
          fontSize: '1.5rem',
          fontWeight: 'bold',
        }}
      >
        {guest.name.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ ml: 2, flex: 1 }}>
        <Typography
          variant="subtitle1"
          component="div"
          sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}
        >
          {guest.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Grupo: {guest.location || 'Sem grupo'}
        </Typography>
      </Box>

      {/* Botões de contato */}
      {!isSelectionMode && (
        <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
          {guest.phone && (
            <IconButton
              onClick={handleWhatsApp}
              size="small"
              sx={{
                color: '#25D366',
                '&:hover': { backgroundColor: '#25D36620' }
              }}
            >
              <WhatsApp fontSize="small" />
            </IconButton>
          )}
          {guest.email && (
            <IconButton
              onClick={handleEmail}
              size="small"
              sx={{
                color: theme.colors.primary,
                '&:hover': { backgroundColor: `${theme.colors.primary}20` }
              }}
            >
              <Email fontSize="small" />
            </IconButton>
          )}
          {guest.email && isHostlyUserState && (
            <IconButton
              onClick={handleNotification}
              size="small"
              sx={{
                color: '#FF6B6B',
                '&:hover': { backgroundColor: '#FF6B6B20' }
              }}
            >
              <NotificationsIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {!isSelectionMode && (
        <>
          <IconButton onClick={handleMenuOpen} size="small">
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleEdit}>
              <Edit sx={{ mr: 1, fontSize: '1rem' }} /> Editar
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <Delete sx={{ mr: 1, fontSize: '1rem' }} /> Excluir
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export const GuestsPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, loading: authLoading } = useAuth();
  const { plan } = useSubscription();
  const {
    canAddGuest,
    guestsRemaining,
    isLoading: limitsLoading,
    checkGuestLimit,
    refreshLimits,
  } = useLimits();
  const { bottom: safeBottom } = useSafeArea();

  const navigate = useNavigate();
  const [guests, setGuests] = useState<GuestType[]>([]);
  const [guestsLoading, setGuestsLoading] = useState(true);
  const [limitDialog, setLimitDialog] = useState({ open: false, message: '' });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState<GuestType | null>(null);
  const [editingGuest, setEditingGuest] = useState<GuestType | null>(null);
  const [newGuestName, setNewGuestName] = useState('');
  const [newGuestLastName, setNewGuestLastName] = useState('');
  const [newGuestEmail, setNewGuestEmail] = useState('');
  const [newGuestPhone, setNewGuestPhone] = useState('');
  const [newGuestZipCode, setNewGuestZipCode] = useState('');
  const [newGuestAddress, setNewGuestAddress] = useState('');
  const [newGuestNumber, setNewGuestNumber] = useState('');
  const [newGuestComplement, setNewGuestComplement] = useState('');
  const [newGuestNeighborhood, setNewGuestNeighborhood] = useState('');
  const [newGuestCity, setNewGuestCity] = useState('');
  const [newGuestState, setNewGuestState] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [newGuestBirthday, setNewGuestBirthday] = useState('');
  const [newGuestGroup, setNewGuestGroup] = useState('');
  const [newGuestNotes, setNewGuestNotes] = useState('');
  const [newGuestAllergies, setNewGuestAllergies] = useState('');
  const [newGuestDrinks, setNewGuestDrinks] = useState('');
  const [newGuestPreferences, setNewGuestPreferences] = useState('');
  const [newGuestMaritalStatus, setNewGuestMaritalStatus] = useState<'single' | 'married' | 'divorced' | 'widowed' | 'other' | ''>('');
  const [newGuestHasChildren, setNewGuestHasChildren] = useState(false);
  const [newGuestChildrenCount, setNewGuestChildrenCount] = useState('');
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });
  const [phoneError, setPhoneError] = useState('');
  const [emailSuggestions, setEmailSuggestions] = useState<string[]>([]);
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState({
    open: false,
    guest: null as GuestType | null,
    sendToAll: false
  });
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [birthdayInput, setBirthdayInput] = useState(''); // Estado para o input mascarado (DD/MM/YYYY)
  const [showEventSelectDialog, setShowEventSelectDialog] = useState(false);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [selectedEventForInvite, setSelectedEventForInvite] = useState<Event | null>(null);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Formatação de data (DD/MM/YYYY)
  const handleBirthdayChange = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');

    // Limita a 8 dígitos
    const cleanValue = numbers.slice(0, 8);

    let formattedValue = cleanValue;
    if (cleanValue.length > 4) {
      formattedValue = `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}/${cleanValue.slice(4)}`;
    } else if (cleanValue.length > 2) {
      formattedValue = `${cleanValue.slice(0, 2)}/${cleanValue.slice(2)}`;
    }

    setBirthdayInput(formattedValue);

    // Se tiver preenchido completamente (8 dígitos), valida e salva no formato YYYY-MM-DD
    if (cleanValue.length === 8) {
      const day = parseInt(cleanValue.slice(0, 2));
      const month = parseInt(cleanValue.slice(2, 4));
      const year = parseInt(cleanValue.slice(4));

      const isValidDate = day > 0 && day <= 31 && month > 0 && month <= 12 && year > 1900 && year <= new Date().getFullYear();

      if (isValidDate) {
        // Verifica dias no mês
        const date = new Date(year, month - 1, day);
        if (date.getDate() === day && date.getMonth() === month - 1) {
          setNewGuestBirthday(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
        }
      }
    } else {
      setNewGuestBirthday(''); // Limpa se estiver incompleto
    }
  };

  // Formatação automática de telefone
  const stripCountryCode = (digits: string) => {
    if (digits.length >= 12 && digits.startsWith('55')) {
      return digits.slice(2);
    }
    return digits;
  };

  const formatPhone = (value: string) => {
    const cleanValue = stripCountryCode(value.replace(/\D/g, ''));

    // Aplica a máscara conforme o tamanho
    if (cleanValue.length <= 2) {
      return cleanValue;
    } else if (cleanValue.length <= 7) {
      return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2)}`;
    } else if (cleanValue.length <= 11) {
      return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2, 7)}-${cleanValue.slice(7)}`;
    } else {
      // Limita a 11 dígitos
      return `(${cleanValue.slice(0, 2)}) ${cleanValue.slice(2, 7)}-${cleanValue.slice(7, 11)}`;
    }
  };

  // Validação de telefone brasileiro
  const validatePhone = (phone: string) => {
    const phoneRegex = /^(1[1-9]|2[12478]|3([1-5]|[7-8])|4[1-9]|5(1|[3-5])|6[1-9]|7[134579]|8[1-9]|9[1-9])9[0-9]{8}$/;
    const cleanPhone = stripCountryCode(phone.replace(/\D/g, ''));

    if (!cleanPhone) {
      setPhoneError('');
      return true;
    }

    if (!phoneRegex.test(cleanPhone)) {
      setPhoneError('Telefone inválido. Use formato: (DDD)9XXXX-XXXX');
      return false;
    }

    setPhoneError('');
    return true;
  };

  // Autocomplete de email
  const handleEmailChange = (value: string) => {
    setNewGuestEmail(value);

    if (value.includes('@')) {
      setShowEmailSuggestions(false);
      return;
    }

    if (value.length > 0) {
      const domains = ['@gmail.com', '@outlook.com', '@hotmail.com', '@yahoo.com'];
      const suggestions = domains.map(domain => value + domain);
      setEmailSuggestions(suggestions);
      setShowEmailSuggestions(true);
    } else {
      setShowEmailSuggestions(false);
    }
  };

  const selectEmailSuggestion = (email: string) => {
    setNewGuestEmail(email);
    setShowEmailSuggestions(false);
  };

  const handleSendEmail = () => {
    if (newGuestEmail) {
      window.location.href = `mailto:${newGuestEmail}`;
    }
  };

  useEffect(() => {
    const loadGuests = async () => {
      if (!user?.uid) {
        setGuestsLoading(false);
        return;
      }

      try {
        setGuestsLoading(true);
        const userGuests = await getUserGuests(user.uid);
        setGuests(userGuests);
      } catch (error) {
        console.error('Erro ao carregar convidados:', error);
        setGuests([]);
      } finally {
        setGuestsLoading(false);
      }
    };

    loadGuests();
  }, [user?.uid]);

  const handleAddGuest = async () => {
    try {
      const limitCheck = await checkGuestLimit();

      if (!limitCheck.allowed) {
        setLimitDialog({
          open: true,
          message: limitCheck.message || 'Limite de convidados atingido',
        });
        return;
      }

      setEditingGuest(null);
      setNewGuestName('');
      setNewGuestLastName('');
      setNewGuestEmail('');
      setNewGuestPhone('');
      setNewGuestZipCode('');
      setNewGuestAddress('');
      setNewGuestNumber('');
      setNewGuestComplement('');
      setNewGuestNeighborhood('');
      setNewGuestCity('');
      setNewGuestState('');
      setNewGuestBirthday('');
      setNewGuestGroup('');
      setNewGuestNotes('');
      setNewGuestAllergies('');
      setNewGuestDrinks('');
      setNewGuestPreferences('');
      setNewGuestMaritalStatus('');
      setNewGuestHasChildren(false);
      setNewGuestChildrenCount('');
      setPhoneError('');
      setShowEmailSuggestions(false);
      setShowCreateForm(true);
    } catch (error) {
      console.error('Erro ao verificar limite:', error);
    }
  };

  const handleSearchCep = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) return;

    try {
      setLoadingCep(true);
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setSnackbar({
          open: true,
          message: 'CEP não encontrado',
          severity: 'error'
        });
        return;
      }

      setNewGuestAddress(data.logradouro || '');
      setNewGuestNeighborhood(data.bairro || '');
      setNewGuestCity(data.localidade || '');
      setNewGuestState(data.uf || '');
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar CEP',
        severity: 'error'
      });
    } finally {
      setLoadingCep(false);
    }
  };

  const handleEditGuest = (guest: GuestType) => {
    setEditingGuest(guest);
    setNewGuestName(guest.name);
    setNewGuestLastName(guest.lastName || '');
    setNewGuestEmail(guest.email || '');
    setNewGuestPhone(guest.phone || '');
    setNewGuestZipCode(guest.zipCode || '');
    setNewGuestAddress(guest.address || '');
    setNewGuestNumber(guest.number || '');
    setNewGuestComplement(guest.complement || '');
    setNewGuestNeighborhood(guest.neighborhood || '');
    setNewGuestCity(guest.city || '');
    setNewGuestState(guest.state || '');
    setNewGuestBirthday(guest.birthday || '');

    // Set masked input from stored date (YYYY-MM-DD -> DD/MM/YYYY)
    if (guest.birthday) {
      const [year, month, day] = guest.birthday.split('-');
      setBirthdayInput(`${day}/${month}/${year}`);
    } else {
      setBirthdayInput('');
    }

    setNewGuestGroup(guest.location || '');
    setNewGuestNotes(guest.notes || '');
    setNewGuestAllergies(guest.allergies || '');
    setNewGuestDrinks(guest.drinks?.join(', ') || '');
    setNewGuestPreferences(guest.preferences?.join(', ') || '');
    setNewGuestMaritalStatus(guest.maritalStatus || '');
    setNewGuestHasChildren(guest.hasChildren || false);
    setNewGuestChildrenCount(guest.childrenCount?.toString() || '');
    setPhoneError('');
    setShowEmailSuggestions(false);
    setShowCreateForm(true);
  };

  const handleSubmitGuest = async () => {
    if (!newGuestName.trim() || !user?.uid) return;

    // Validar telefone antes de enviar
    if (newGuestPhone && !validatePhone(newGuestPhone)) {
      return;
    }

    try {
      setCreating(true);

      const guestData = {
        name: newGuestName.trim(),
        lastName: newGuestLastName.trim() || undefined,
        email: newGuestEmail.trim() || undefined,
        phone: newGuestPhone.trim() || undefined,
        zipCode: newGuestZipCode.trim() || undefined,
        address: newGuestAddress.trim() || undefined,
        number: newGuestNumber.trim() || undefined,
        complement: newGuestComplement.trim() || undefined,
        neighborhood: newGuestNeighborhood.trim() || undefined,
        city: newGuestCity.trim() || undefined,
        state: newGuestState.trim() || undefined,
        birthday: newGuestBirthday.trim()
          ? newGuestBirthday.trim()
          : (birthdayInput.trim() ? (editingGuest?.birthday || undefined) : undefined),
        location: newGuestGroup.trim() || 'Sem grupo',
        notes: newGuestNotes.trim() || undefined,
        allergies: newGuestAllergies.trim() || undefined,
        drinks: newGuestDrinks.trim() ? newGuestDrinks.split(',').map(d => d.trim()).filter(d => d) : undefined,
        preferences: newGuestPreferences.trim() ? newGuestPreferences.split(',').map(p => p.trim()).filter(p => p) : undefined,
        maritalStatus: newGuestMaritalStatus || undefined,
        hasChildren: newGuestHasChildren || undefined,
        childrenCount: newGuestHasChildren && newGuestChildrenCount ? parseInt(newGuestChildrenCount) : undefined,
        status: 'pending' as const,
      };

      if (editingGuest && editingGuest.id) {
        // Atualizar convidado existente
        await updateGuest(editingGuest.id, guestData);
      } else {
        // Criar novo convidado
        await createGuest(user.uid, guestData);
      }

      const userGuests = await getUserGuests(user.uid);
      setGuests(userGuests);
      await refreshLimits();

      setNewGuestName('');
      setNewGuestLastName('');
      setNewGuestEmail('');
      setNewGuestPhone('');
      setNewGuestZipCode('');
      setNewGuestAddress('');
      setNewGuestNumber('');
      setNewGuestComplement('');
      setNewGuestNeighborhood('');
      setNewGuestCity('');
      setNewGuestState('');
      setNewGuestBirthday('');
      setBirthdayInput('');
      setNewGuestGroup('');
      setNewGuestNotes('');
      setNewGuestAllergies('');
      setNewGuestDrinks('');
      setNewGuestPreferences('');
      setNewGuestMaritalStatus('');
      setNewGuestHasChildren(false);
      setNewGuestChildrenCount('');
      setShowCreateForm(false);
      setEditingGuest(null);
      setSnackbar({
        open: true,
        message: editingGuest ? 'Convidado atualizado com sucesso!' : 'Convidado adicionado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao salvar convidado:', error);
      setSnackbar({ open: true, message: 'Erro ao salvar convidado. Tente novamente.', severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGuest = async (guestId: string) => {
    if (!user?.uid) return;

    try {
      await deleteGuest(guestId, user.uid);
      const userGuests = await getUserGuests(user.uid);
      setGuests(userGuests);
      await refreshLimits();
      setSnackbar({ open: true, message: 'Convidado excluído com sucesso!', severity: 'success' });
    } catch (error) {
      console.error('Erro ao excluir convidado:', error);
      setSnackbar({ open: true, message: 'Erro ao excluir convidado. Tente novamente.', severity: 'error' });
    }
  };

  const handleDeleteClick = (guest: GuestType) => {
    setGuestToDelete(guest);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (guestToDelete?.id) {
      await handleDeleteGuest(guestToDelete.id);
    }
    setShowDeleteDialog(false);
    setGuestToDelete(null);
  };

  const handleGuestClick = (guestId: string) => {
    navigate(`/guests/${guestId}`);
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleSendNotification = (guest: GuestType) => {
    setNotificationDialog({ open: true, guest, sendToAll: false });
    setNotificationMessage(`Olá ${guest.name}! Você foi convidado para meu evento. Acesse o app Hostly para mais detalhes.`);
  };

  const handleSelectGuest = (guestId: string) => {
    setSelectedGuests(prev =>
      prev.includes(guestId)
        ? prev.filter(id => id !== guestId)
        : [...prev, guestId]
    );
  };

  const handleSelectAll = () => {
    const guestsWithEmail = guests.filter(g => g.email && g.id).map(g => g.id!);
    setSelectedGuests(prev =>
      prev.length === guestsWithEmail.length ? [] : guestsWithEmail
    );
  };

  const handleSendToSelected = async () => {
    if (selectedGuests.length === 0) return;
    // Fetch user's upcoming events and show event selection dialog
    try {
      setLoadingEvents(true);
      if (user?.uid) {
        const events = await getUserEvents(user.uid);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const upcomingEvents = events.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0);
          return eventDate >= now;
        }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setUserEvents(upcomingEvents);
      }
      setShowEventSelectDialog(true);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar eventos', severity: 'error' });
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleEventSelectedForInvite = (event: Event) => {
    setSelectedEventForInvite(event);
    setShowEventSelectDialog(false);
    // Build notification message with event details
    const eventDate = typeof event.date === 'string'
      ? new Date(event.date + 'T00:00:00')
      : event.date instanceof Date ? event.date : new Date(event.date);
    const dateStr = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr = event.time || '';
    const locationStr = event.location ? ` Local: ${event.location}.` : '';
    const message = `Olá! Você foi convidado para o evento '${event.title}' em ${dateStr}${timeStr ? ` às ${timeStr}` : ''}.${locationStr} Acesse o app Hostly para mais detalhes.`;
    setNotificationMessage(message);
    setNotificationDialog({ open: true, guest: null, sendToAll: false });
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedGuests([]);
    }
  };

  const handleConfirmSendNotification = async () => {
    if (!user) return;

    try {
      setSendingNotification(true);

      if (notificationDialog.sendToAll) {
        // Enviar para todos os convidados com email
        const guestsWithEmail = guests.filter(g => g.email);

        if (guestsWithEmail.length === 0) {
          setSnackbar({
            open: true,
            message: 'Nenhum convidado possui email cadastrado',
            severity: 'info'
          });
          return;
        }

        const emails = guestsWithEmail.map(g => g.email!);
        const result = await sendBulkGuestNotifications(
          emails,
          user.uid,
          user.displayName || user.email || 'Um anfitrião',
          notificationMessage,
          {}
        );

        if (result.sent > 0) {
          setSnackbar({
            open: true,
            message: `${result.sent} notificação(ões) enviada(s) com sucesso!${result.notHostlyUsers > 0 ? ` ${result.notHostlyUsers} não são usuários do Hostly.` : ''}`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Nenhum convidado é usuário do Hostly',
            severity: 'info'
          });
        }
      } else if (selectedGuests.length > 0) {
        // Enviar para convidados selecionados
        const selectedGuestsWithEmail = guests.filter(g => selectedGuests.includes(g.id!) && g.email);

        if (selectedGuestsWithEmail.length === 0) {
          setSnackbar({
            open: true,
            message: 'Nenhum convidado selecionado possui email cadastrado',
            severity: 'info'
          });
          return;
        }

        const metadata: any = {};
        if (selectedEventForInvite) {
          metadata.eventId = selectedEventForInvite.id;
          metadata.eventTitle = selectedEventForInvite.title;
          metadata.time = selectedEventForInvite.time || '';
          metadata.location = selectedEventForInvite.location || '';
        }

        const result = await sendBulkGuestNotifications(
          selectedGuestsWithEmail.map(g => g.email!),
          user.uid,
          user.displayName || user.email || 'Um anfitrião',
          notificationMessage,
          metadata
        );

        if (result.sent > 0) {
          setSnackbar({
            open: true,
            message: `${result.sent} notificação(ões) enviada(s) com sucesso!`,
            severity: 'success'
          });
          setSelectedGuests([]);
          setSelectedEventForInvite(null);
        } else {
          setSnackbar({
            open: true,
            message: 'Nenhum convidado selecionado é usuário do Hostly',
            severity: 'info'
          });
        }
      } else if (notificationDialog.guest?.email) {
        // Enviar para um convidado específico
        const sent = await sendGuestNotification(
          notificationDialog.guest.email,
          user.uid,
          user.displayName || user.email || 'Um anfitrião',
          notificationMessage,
          {
            guestId: notificationDialog.guest.id,
          }
        );

        if (sent) {
          setSnackbar({
            open: true,
            message: 'Notificação enviada com sucesso!',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Este convidado não é usuário do Hostly',
            severity: 'info'
          });
        }
      }
    } catch (error) {
      console.error('Erro ao enviar notificação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao enviar notificação',
        severity: 'error'
      });
    } finally {
      setSendingNotification(false);
      setNotificationDialog({ open: false, guest: null, sendToAll: false });
      setNotificationMessage('');
      setSelectedEventForInvite(null);
    }
  };

  if (authLoading || limitsLoading) {
    return (
      <>
        <TopBar
          title="Convidados"
          icon={<People sx={{ fontSize: '0.875rem' }} />}
          showBackButton={true}
          onBackClick={() => navigate('/dashboard')}
        />
        <Box sx={{ paddingTop: '70px', padding: 2, maxWidth: '1200px', margin: '0 auto' }}>
          <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2, mb: 2 }} />
          <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid
                key={item}
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const leftColumnGuests = guests.filter((_, index) => index % 2 === 0);
  const rightColumnGuests = guests.filter((_, index) => index % 2 === 1);
  const guestsWithEmail = guests.filter(g => g.email && g.id);

  return (
    <>
      <TopBar
        title="Convidados"
        icon={<People sx={{ fontSize: '0.875rem' }} />}
        showBackButton={true}
        showProfileButton={true}
        showSearchButton={true}
        showMoreButton={true}
        onBackClick={() => navigate('/dashboard')}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        onMoreClick={() => setIsSelectionMode(!isSelectionMode)}
      />
      <Box sx={{ padding: 2, paddingBottom: '120px', paddingTop: '70px', maxWidth: '1200px', margin: '0 auto', minHeight: '100vh' }}>
        {guestsRemaining <= 5 && guestsRemaining > 0 && (
          <UpgradePrompt feature="convidados" currentPlan={plan} onUpgrade={handleUpgrade} />
        )}

        <Button
          startIcon={<Add />}
          fullWidth
          onClick={handleAddGuest}
          disabled={!canAddGuest}
          sx={{
            backgroundColor: theme.colors.secondary,
            color: theme.colors.text.onSecondary,
            borderRadius: theme.radii.medium,
            padding: '12px',
            fontSize: '0.875rem',
            fontWeight: 'bold',
            textTransform: 'none',
            marginBottom: 3,
            boxShadow: theme.shadows.small,
            '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9 },
          }}
        >
          Adicionar Convidado
          {guestsRemaining > 0 && guestsRemaining !== -1 && (
            <Typography component="span" sx={{ ml: 1, fontSize: '0.75rem', opacity: 0.9 }}>
              ({guestsRemaining} vagas)
            </Typography>
          )}
        </Button>

        {/* Botões de ação */}
        {guests.length > 0 && guestsWithEmail.length > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {!isSelectionMode ? (
              <>
                <Button
                  startIcon={<CheckCircle />}
                  fullWidth
                  onClick={toggleSelectionMode}
                  sx={{
                    backgroundColor: theme.colors.primary,
                    color: theme.colors.text.contrast,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    boxShadow: theme.shadows.small,
                    '&:hover': { backgroundColor: theme.colors.primary, opacity: 0.9 },
                  }}
                >
                  Selecionar Convidados
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<Cancel />}
                  onClick={toggleSelectionMode}
                  sx={{
                    color: theme.colors.text.secondary,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSelectAll}
                  sx={{
                    color: theme.colors.primary,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                  }}
                >
                  {selectedGuests.length === guestsWithEmail.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                </Button>
                <Button
                  startIcon={<NotificationsIcon />}
                  onClick={handleSendToSelected}
                  disabled={selectedGuests.length === 0}
                  variant="contained"
                  sx={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.text.onSecondary,
                    borderRadius: theme.radii.medium,
                    padding: '12px',
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    boxShadow: theme.shadows.small,
                    '&:hover': { backgroundColor: theme.colors.secondary, opacity: 0.9 },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                    }
                  }}
                >
                  Incluir em evento ({selectedGuests.length})
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Chip de selecionados */}
        {isSelectionMode && selectedGuests.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={`${selectedGuests.length} convidado(s) selecionado(s)`}
              color="primary"
              onDelete={() => setSelectedGuests([])}
            />
          </Box>
        )}

        {guestsLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid
                key={item}
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : guests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, backgroundColor: theme.colors.card, borderRadius: 2 }}>
            <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
              Nenhum convidado cadastrado ainda
            </Typography>
            <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', mt: 1 }}>
              Adicione seus primeiros convidados!
            </Typography>
          </Box>
        ) : (
          <>
            {/* Mobile View - Single Column */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
                {guests.map((guest, index) => (
                  <React.Fragment key={guest.id}>
                    <GuestItem
                      guest={guest}
                      onEdit={handleEditGuest}
                      onDelete={handleDeleteClick}
                      onClick={handleGuestClick}
                      onSendNotification={handleSendNotification}
                      theme={theme}
                      isSelectionMode={isSelectionMode}
                      isSelected={selectedGuests.includes(guest.id!)}
                      onSelect={handleSelectGuest}
                    />
                    {index < guests.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Paper>
            </Box>

            {/* Desktop View - Two Columns */}
            <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
                  {leftColumnGuests.map((guest, index) => (
                    <React.Fragment key={guest.id}>
                      <GuestItem
                        guest={guest}
                        onEdit={handleEditGuest}
                        onDelete={handleDeleteClick}
                        onClick={handleGuestClick}
                        onSendNotification={handleSendNotification}
                        theme={theme}
                        isSelectionMode={isSelectionMode}
                        isSelected={selectedGuests.includes(guest.id!)}
                        onSelect={handleSelectGuest}
                      />
                      {index < leftColumnGuests.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Paper>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 'none' }}>
                  {rightColumnGuests.map((guest, index) => (
                    <React.Fragment key={guest.id}>
                      <GuestItem
                        guest={guest}
                        onEdit={handleEditGuest}
                        onDelete={handleDeleteClick}
                        onClick={handleGuestClick}
                        onSendNotification={handleSendNotification}
                        theme={theme}
                        isSelectionMode={isSelectionMode}
                        isSelected={selectedGuests.includes(guest.id!)}
                        onSelect={handleSelectGuest}
                      />
                      {index < rightColumnGuests.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        <BottomNavigation />

        <LimitReachedDialog
          open={limitDialog.open}
          feature="convidados"
          onClose={() => setLimitDialog({ open: false, message: '' })}
          onUpgrade={handleUpgrade}
          currentPlan={plan}
        />

        {/* Dialog de confirmação de exclusão */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Excluir Convidado"
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setShowDeleteDialog(false)}
                sx={{ color: theme.colors.text.secondary }}
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDelete}
                variant="contained"
                sx={{
                  backgroundColor: '#F44336',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#D32F2F',
                  }
                }}
              >
                Excluir
              </Button>
            </Box>
          }
        >
          <Box sx={{ py: 2 }}>
            <Typography>
              Deseja realmente excluir <strong>{guestToDelete?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Esta ação não pode ser desfeita.
            </Typography>
          </Box>
        </Dialog>

        {/* Dialog de criação/edição */}
        <Dialog
          open={showCreateForm}
          onClose={() => {
            setShowCreateForm(false);
            setEditingGuest(null);
            setPhoneError('');
            setShowEmailSuggestions(false);
          }}
          title={editingGuest ? 'Editar Convidado' : 'Adicionar Convidado'}
          maxWidth="lg"
          fullWidth
          fullScreen={window.innerWidth < 600}
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => {
                setShowCreateForm(false);
                setPhoneError('');
                setShowEmailSuggestions(false);
              }} sx={{ color: theme.colors.text.secondary }}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmitGuest}
                disabled={!newGuestName.trim() || creating || !!phoneError}
                variant="contained"
                sx={{ backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }}
              >
                {creating ? <CircularProgress size={20} color="inherit" /> : editingGuest ? 'Salvar' : 'Adicionar'}
              </Button>
            </Box>
          }
        >
          <Box sx={{
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            maxHeight: `calc(85vh - ${safeBottom}px)`,
            overflowY: 'auto',
            px: 2,
          }}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Nome *"
                  value={newGuestName}
                  onChange={(e) => setNewGuestName(e.target.value)}
                  required
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  label="Sobrenome"
                  value={newGuestLastName}
                  onChange={(e) => setNewGuestLastName(e.target.value)}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Grupo"
              value={newGuestGroup}
              onChange={(e) => setNewGuestGroup(e.target.value)}
              placeholder="Ex: Faculdade"
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1.5 }}>
                Notas:
              </Typography>
              <TextField
                fullWidth
                value={newGuestNotes}
                onChange={(e) => setNewGuestNotes(e.target.value)}
                placeholder="Ex: Minha amiga da faculdade. Extrovertida."
                multiline
                rows={3}
              />
            </Box>

            <Divider />

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1.5 }}>
                    Alergias/Intolerâncias:
                  </Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={Object.keys(allergiesData)}
                    value={newGuestAllergies ? newGuestAllergies.split(',').map(s => s.trim()).filter(Boolean) : []}
                    onChange={(_, newValue) => {
                      const cleanValue = newValue.map((v) => typeof v === 'string' ? v : String(v)).join(', ');
                      setNewGuestAllergies(cleanValue);
                    }}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip key={key} variant="outlined" label={option} {...tagProps} />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Ex: Alergia a camarão"
                        fullWidth
                        helperText="Selecione da lista ou digite e pressione Enter"
                      />
                    )}
                  />
                </Box>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1.5 }}>
                    Bebe:
                  </Typography>
                  <TextField
                    fullWidth
                    value={newGuestDrinks}
                    onChange={(e) => setNewGuestDrinks(e.target.value)}
                    placeholder="Ex: Coca Zero, Vinho branco"
                    multiline
                    rows={2}
                  />
                </Box>
              </Grid>
            </Grid>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 1.5 }}>
                Preferências:
              </Typography>
              <TextField
                fullWidth
                value={newGuestPreferences}
                onChange={(e) => setNewGuestPreferences(e.target.value)}
                placeholder="Ex: Carne bem passada, Não gosta de peixe"
                multiline
                rows={3}
              />
            </Box>

            <Divider />

            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mt: 1 }}>
              Dados de Contato:
            </Typography>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={newGuestPhone}
                  onChange={(e) => {
                    const formatted = formatPhone(e.target.value);
                    setNewGuestPhone(formatted);
                    validatePhone(formatted);
                  }}
                  type="tel"
                  error={!!phoneError}
                  helperText={phoneError || 'Formato: (DDD)9XXXX-XXXX'}
                  placeholder="Ex: 11999999999"
                  slotProps={{
                    htmlInput: {
                      inputMode: 'tel',
                      pattern: '[0-9]*',
                      autoComplete: 'tel',
                    }
                  }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box sx={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={newGuestEmail}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onFocus={() => {
                      if (newGuestEmail && !newGuestEmail.includes('@')) {
                        setShowEmailSuggestions(true);
                      }
                    }}
                    type="email"
                    slotProps={{
                      input: {
                        endAdornment: newGuestEmail && (
                          <IconButton onClick={handleSendEmail} size="small">
                            <Email />
                          </IconButton>
                        ),
                      }
                    }}
                  />
                  {showEmailSuggestions && emailSuggestions.length > 0 && (
                    <Paper
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 200,
                        overflow: 'auto',
                        mt: 0.5,
                      }}
                    >
                      <List dense>
                        {emailSuggestions.map((suggestion, index) => (
                          <ListItemButton
                            key={index}
                            onClick={() => selectEmailSuggestion(suggestion)}
                            sx={{
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                            }}
                          >
                            <ListItemText primary={suggestion} />
                          </ListItemButton>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="CEP"
                  value={newGuestZipCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setNewGuestZipCode(value);
                    if (value.length === 8) {
                      handleSearchCep(value);
                    }
                  }}
                  placeholder="00000-000"
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      endAdornment: loadingCep && <CircularProgress size={20} />
                    }
                  }}
                  inputProps={{ maxLength: 8 }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Endereço"
                  value={newGuestAddress}
                  onChange={(e) => setNewGuestAddress(e.target.value)}
                  placeholder="Ex: Rua das Flores"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Número"
                  value={newGuestNumber}
                  onChange={(e) => setNewGuestNumber(e.target.value)}
                  placeholder="123"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <TextField
                  fullWidth
                  label="Complemento / Apto"
                  value={newGuestComplement}
                  onChange={(e) => setNewGuestComplement(e.target.value)}
                  placeholder="Ex: Apto 101, Bloco A"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Bairro"
                  value={newGuestNeighborhood}
                  onChange={(e) => setNewGuestNeighborhood(e.target.value)}
                  placeholder="Ex: Centro"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  fullWidth
                  label="Cidade"
                  value={newGuestCity}
                  onChange={(e) => setNewGuestCity(e.target.value)}
                  placeholder="Ex: São Paulo"
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="Estado"
                  value={newGuestState}
                  onChange={(e) => setNewGuestState(e.target.value)}
                  placeholder="Ex: SP"
                  inputProps={{ maxLength: 2 }}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Data de Aniversário"
              value={birthdayInput}
              onChange={(e) => handleBirthdayChange(e.target.value)}
              placeholder="DD/MM/AAAA"
              slotProps={{
                inputLabel: { shrink: true }
              }}
              inputProps={{ maxLength: 10 }}
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, mb: 2 }}>
              Informações Pessoais:
            </Typography>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  select
                  label="Estado Civil"
                  value={newGuestMaritalStatus || ''}
                  onChange={(e) => setNewGuestMaritalStatus(e.target.value as '' | 'single' | 'married' | 'divorced' | 'widowed' | 'other')}
                >
                  <MenuItem value="">Não informado</MenuItem>
                  <MenuItem value="single">Solteiro(a)</MenuItem>
                  <MenuItem value="married">Casado(a)</MenuItem>
                  <MenuItem value="divorced">Divorciado(a)</MenuItem>
                  <MenuItem value="widowed">Viúvo(a)</MenuItem>
                  <MenuItem value="other">Outro</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    select
                    label="Tem filhos?"
                    value={newGuestHasChildren ? 'yes' : 'no'}
                    onChange={(e) => {
                      const hasChildren = e.target.value === 'yes';
                      setNewGuestHasChildren(hasChildren);
                      if (!hasChildren) {
                        setNewGuestChildrenCount('');
                      }
                    }}
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="no">Não</option>
                    <option value="yes">Sim</option>
                  </TextField>
                  {newGuestHasChildren && (
                    <TextField
                      label="Quantidade"
                      type="number"
                      value={newGuestChildrenCount}
                      onChange={(e) => setNewGuestChildrenCount(e.target.value)}
                      placeholder="1"
                      sx={{ width: 120 }}
                      slotProps={{
                        htmlInput: { min: 1, max: 20 }
                      }}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Dialog>

        {/* Dialog de seleção de evento para envio */}
        <Dialog
          open={showEventSelectDialog}
          onClose={() => {
            setShowEventSelectDialog(false);
            setSelectedEventForInvite(null);
          }}
          title="Selecionar Evento"
          actions={
            <Button
              onClick={() => {
                setShowEventSelectDialog(false);
                setSelectedEventForInvite(null);
              }}
              sx={{ color: theme.colors.text.secondary }}
            >
              Cancelar
            </Button>
          }
        >
          <Box sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ mb: 2, color: theme.colors.text.secondary }}>
              Escolha o evento para o qual deseja enviar o convite:
            </Typography>
            {loadingEvents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={32} />
              </Box>
            ) : userEvents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.875rem' }}>
                  Nenhum evento futuro encontrado
                </Typography>
                <Typography sx={{ color: theme.colors.text.secondary, fontSize: '0.75rem', mt: 1 }}>
                  Crie um evento primeiro na aba Eventos
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {userEvents.map((event) => {
                  const eventDate = typeof event.date === 'string'
                    ? new Date(event.date + 'T00:00:00')
                    : event.date instanceof Date ? event.date : new Date(event.date);
                  const dateStr = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
                  return (
                    <ListItemButton
                      key={event.id}
                      onClick={() => handleEventSelectedForInvite(event)}
                      sx={{
                        borderRadius: theme.radii?.small || 1,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: theme.colors.secondary + '20',
                        },
                      }}
                    >
                      <ListItemText
                        primary={event.title}
                        secondary={`${dateStr}${event.time ? ` - ${event.time}` : ''}${event.location ? ` | ${event.location}` : ''}`}
                        primaryTypographyProps={{ fontWeight: 'bold', color: theme.colors.text.primary }}
                        secondaryTypographyProps={{ color: theme.colors.text.secondary }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </Dialog>

        {/* Dialog de envio de notificação */}
        <Dialog
          open={notificationDialog.open}
          onClose={() => setNotificationDialog({ open: false, guest: null, sendToAll: false })}
          title={notificationDialog.sendToAll ? 'Enviar Notificação para Todos' : 'Enviar Notificação'}
          actions={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setNotificationDialog({ open: false, guest: null, sendToAll: false })}
                sx={{ color: theme.colors.text.secondary }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmSendNotification}
                disabled={sendingNotification || !notificationMessage.trim()}
                variant="contained"
                sx={{ backgroundColor: theme.colors.primary, color: theme.colors.text.contrast }}
              >
                {sendingNotification ? <CircularProgress size={20} color="inherit" /> : 'Enviar'}
              </Button>
            </Box>
          }
        >
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {notificationDialog.sendToAll ? (
                <>
                  Enviar notificação para <strong>todos os convidados</strong> com email cadastrado
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: theme.colors.text.secondary }}>
                    ({guests.filter(g => g.email).length} convidado(s))
                  </Typography>
                </>
              ) : (
                <>
                  Enviar notificação para <strong>{notificationDialog.guest?.name}</strong>
                </>
              )}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={notificationMessage}
              onChange={(e) => setNotificationMessage(e.target.value)}
              placeholder="Digite sua mensagem..."
            />
          </Box>
        </Dialog>

        {/* Snackbar para mensagens de sucesso/erro */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};